<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import AddStudentList from '@/components/Dialogs/Forms/AddStudentList.vue';
import StudentGoodMoralDialog from '@/components/Dialogs/Forms/StudentGoodMoralDialog.vue';
import ViewStudentFinalGradeDialog from '@/components/Dialogs/Views/ViewStudentFinalGradeDialog.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useOpenPdf } from '@/composables/useOpenPdf';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { titleCase } from '@/utils/format';

useSessionGuard();

// The adviser's own class: students grouped by sex, report cards (Form
// 138), values ratings, and dropping or removing students.
const store = useStore();
const year = computed(() => store.getters.getFilterSelected);
const syType = computed(() => store.getters.getSyType);
// Observed values are rated per quarter on the quarterly report card; a
// school year graded in three terms doesn't use them.
const usesObservedValues = computed(() => Number(syType.value) === 0);
const room = shallowRef(null); // { name, grade, id }
const hasNoClass = shallowRef(false);

// Shown (as info, not an error) to teachers who don't advise a class.
const NO_ADVISORY_CLASS = {
  title: 'No advisory class yet',
  text: 'An admin assigns advisers to sections under Rooms. Once you have one, your students show here.',
};

const { items, loading, error, load } = useListResource(async () => {
  const rows = await apiRequest('get', `/rooms-section/getMyClassList/${store.state.user.id}/${year.value}`);
  hasNoClass.value = !Array.isArray(rows) || !rows.length;
  if (hasNoClass.value) {
    room.value = null;
    return [];
  }
  room.value = { name: rows[0].room_name, grade: rows[0].grade_level, id: rows[0].roomID };
  // A class with no students yet comes back as one row with a null name.
  return rows[0].name == null
    ? []
    : rows.map((r) => ({ ...r, name: titleCase(r.name), valuesRated: valuesProgress(r).count }));
});
watch(year, load, { immediate: true });

const headers = computed(() => [
  { title: 'LRN', key: 'lrnNo' },
  { title: 'Name', key: 'name' },
  { title: 'Grades submitted', key: 'subjects', sortable: false, hideable: true },
  ...(usesObservedValues.value ? [{ title: 'Observed values', key: 'valuesRated', hideable: true }] : []),
]);

// --- Grading period and grade submissions ----------------------------------
// The period picked here drives the "Grades submitted" badges and the
// Release grades dialog. Senior High also picks the semester.
const WHOLE_YEAR = 'All';
const isSHS = computed(() => ['Grade 11', 'Grade 12'].includes(room.value?.grade));
const periodOptions = computed(() => {
  if (isSHS.value && syType.value == 0) return ['1st Quarter', '2nd Quarter'];
  return syType.value == 0
    ? ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter', { title: 'Whole year', value: WHOLE_YEAR }]
    : ['1st Term', '2nd Term', '3rd Term'];
});
const semesterOptions = computed(() => (syType.value == 0 ? ['1st Semester', '2nd Semester'] : ['Senior High']));
const period = shallowRef(null);
const semester = shallowRef(null);
watch(
  [room, syType],
  () => {
    period.value = syType.value == 0 ? '1st Quarter' : '1st Term';
    semester.value = isSHS.value ? (syType.value == 1 ? 'Senior High' : '1st Semester') : 'Junior High';
  },
  { immediate: true },
);
const periodLabel = computed(() => (period.value === WHOLE_YEAR ? 'the whole year' : period.value));

// Which subjects have submitted the period, for the whole class and per
// student: { subjects: [{ id, title, teacher, submitted, released }],
// students: { [studentId]: [subjectId] }, missing: [title], released }.
const submissions = shallowRef(null);
async function loadSubmissions() {
  if (!room.value || !period.value) {
    submissions.value = null;
    return;
  }
  try {
    submissions.value = await apiRequest('get', `/rooms-section/gradeSubmissions/${room.value.id}/${year.value}`, null, {
      params: { quarter: period.value, semester: semester.value },
    });
  } catch {
    submissions.value = null;
  }
}
watch([room, year, period, semester], loadSubmissions);
// One student's submissions as a single badge ("6 of 9"), so the row stays
// one line however many subjects the class takes. Hovering (or clicking)
// the badge lists the subjects, split into not yet submitted and submitted.
function gradesProgress(student) {
  const done = new Set(submissions.value?.students?.[student.studentId] ?? []);
  const subjects = submissions.value?.subjects ?? [];
  const submitted = subjects.filter((s) => done.has(s.id));
  const missing = subjects.filter((s) => !done.has(s.id));
  const count = submitted.length;
  const total = subjects.length;
  return {
    groups: [
      { key: 'missing', title: 'Not yet submitted', icon: 'mdi-clock-outline', subjects: missing },
      { key: 'submitted', title: 'Submitted', icon: 'mdi-check-circle', subjects: submitted },
    ].filter((g) => g.subjects.length),
    count,
    total,
    variant: count === total ? 'green' : count ? 'amber' : 'outline',
    text: count === total ? 'Complete' : `${count} of ${total}`,
  };
}

// Dropped students (statusEnrolled 2) are hidden unless asked for.
const STATUS_FILTERS = [
  { value: 'enrolled', title: 'Enrolled' },
  { value: 'dropped', title: 'Dropped' },
  { value: 'all', title: 'All students' },
];
const statusFilter = shallowRef('enrolled');
const isDropped = (s) => Number(s.statusEnrolled) === 2;
const shown = computed(() =>
  statusFilter.value === 'all'
    ? items.value
    : items.value.filter((s) => (statusFilter.value === 'dropped' ? isDropped(s) : !isDropped(s))),
);
const statusCounts = computed(() => {
  const dropped = items.value.filter(isDropped).length;
  return { enrolled: items.value.length - dropped, dropped, all: items.value.length };
});

// The grading periods a report card needs observed values for, as the
// "semester|quarter" pairs the server returns in values_periods.
const valuePeriods = computed(() => {
  const shs = ['Grade 11', 'Grade 12'].includes(room.value?.grade);
  if (shs) {
    return ['1st Semester', '2nd Semester'].flatMap((sem) =>
      ['1st Quarter', '2nd Quarter'].map((q) => ({ key: `${sem}|${q}`, label: `${sem.replace(' Semester', ' Sem')} ${q.replace(' Quarter', ' Q')}` })),
    );
  }
  return ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'].map((q) => ({ key: `Junior High|${q}`, label: q }));
});
function valuesProgress(student) {
  const done = new Set(String(student.values_periods || '').split(',').filter(Boolean));
  const rated = valuePeriods.value.filter((p) => done.has(p.key));
  const total = valuePeriods.value.length;
  return {
    count: rated.length,
    total,
    variant: rated.length === total ? 'green' : rated.length ? 'amber' : 'outline',
    text: rated.length === total ? 'Complete' : rated.length ? `${rated.length} of ${total}` : 'Not started',
    detail: rated.length ? `Rated: ${rated.map((p) => p.label).join(', ')}` : 'No ratings yet',
  };
}
const groupBy = [{ key: 'sex', order: 'asc' }];

// Form 138, with (1) or without (0) the signature; the template depends on
// the school year type. Fetched rather than linked, so when it can't be
// printed (no submitted grades yet, or the student is dropped) the server's
// reason shows as a message instead of an error page.
const openPdf = useOpenPdf();
function printCard(student, signed) {
  const endpoint = syType.value == 0 ? 'getStudentAchievements' : 'getStudentAchievementsV2';
  openPdf(
    `/pdf-generator/${endpoint}/${student.studentId}/${room.value.id}/${year.value}/${encodeURIComponent(student.grade_level)}/${signed}`,
    'Could not print the report card.',
  );
}

// The class's grades for the picked period, to review and release.
const gradesRoom = shallowRef(null);
const gradesOpen = shallowRef(false);

// Observed values uses the newer v-model dialog.
const valuesFor = shallowRef(null);
const valuesOpen = shallowRef(false);
function openValues(student) {
  valuesFor.value = student;
  valuesOpen.value = true;
}
const addOpen = shallowRef(false);
const classLrns = computed(() => items.value.map((s) => s.lrnNo).filter(Boolean));
function openReleaseGrades() {
  gradesRoom.value = { grade_level: room.value.grade, name: room.value.name, roomID: room.value.id, school_yearID: year.value };
  gradesOpen.value = true;
}


const { busy, run } = useAsyncAction();
const { confirm } = useConfirm();

async function remove(student) {
  const ok = await confirm({
    title: 'Remove student?',
    description: `${student.name} will be removed from your class.`,
    confirmText: 'Remove',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('delete', `/rooms-section/removeMyStudent/${student.id}`), { success: 'Student removed.', error: 'Could not remove the student.' }))) {
    load();
  }
}

// Drop needs a reason, so it gets a small form instead of a confirm.
const dropping = shallowRef(null);
const dropOpen = shallowRef(false);
const dropForm = ref({ remarks: '' });
const dropFields = [
  { key: 'remarks', label: 'Reason', type: 'textarea', required: true, placeholder: 'Remarks and reason for dropping out' },
];
function openDrop(student) {
  dropping.value = student;
  dropForm.value = { remarks: '' };
  dropOpen.value = true;
}
async function drop() {
  const ok = await run(
    () =>
      // Drops them from this class for this school year only.
      apiRequest('patch', `/enroll-student/dropStudent/${dropping.value.id}`, {
        remarks: dropForm.value.remarks.trim(),
      }),
    { success: 'Student dropped.', error: 'Could not drop the student.' },
  );
  if (ok) {
    dropOpen.value = false;
    load();
  }
}

const rowActions = (student) => [
  { title: 'Print report card', icon: 'mdi-printer-outline', hidden: student.graded != 1, onClick: () => printCard(student, 1) },
  { title: 'Print without signature', icon: 'mdi-printer-off-outline', hidden: student.graded != 1, onClick: () => printCard(student, 0) },
  { title: 'Values', icon: 'mdi-account-star-outline', hidden: isDropped(student) || !usesObservedValues.value, onClick: () => openValues(student) },
  { title: 'Drop', icon: 'mdi-account-cancel-outline', hidden: isDropped(student), onClick: () => openDrop(student) },
  { title: 'Remove', icon: 'mdi-trash-can-outline', destructive: true, onClick: () => remove(student) },
];
</script>

<template>
  <UiTableCard
    :title="room?.name || 'My class'"
    :description="room ? `${room.grade} · advisory class` : 'Your advisory class.'"
    :headers="headers"
    :items="shown"
    columns-key="advisory-class"
    :loading="loading"
    :error="error"
    :info="hasNoClass ? NO_ADVISORY_CLASS : null"
    :group-by="groupBy"
    groups-open
    :items-per-page="-1"
    search-placeholder="Search students"
    :empty-text="statusFilter === 'dropped' ? 'No dropped students.' : 'No students in your class yet.'"
    row-label="name"
    :row-actions="rowActions"
  >
    <template #actions>
      <v-btn variant="outlined" prepend-icon="mdi-send-check-outline" :disabled="!room" @click="openReleaseGrades">
        Release grades
      </v-btn>
      <v-btn color="primary" prepend-icon="mdi-plus" :disabled="!room" @click="addOpen = true">
        Add students
      </v-btn>
    </template>
    <template #[`item.lrnNo`]="{ item }">
      <span class="text-medium-emphasis">{{ item.lrnNo }}</span>
    </template>
    <template #toolbar>
      <v-select
        v-model="statusFilter"
        :items="STATUS_FILTERS"
        density="compact"
        hide-details
        style="max-width: 200px"
        aria-label="Show students"
      >
        <template #selection="{ item }">
          <span class="status-option">
            {{ item.title }}
            <span class="status-option__count">{{ statusCounts[item.value] }}</span>
          </span>
        </template>
        <template #item="{ item, props: itemProps }">
          <v-list-item v-bind="itemProps">
            <template #title>
              <span class="status-option">
                {{ item.title }}
                <span class="status-option__count">{{ statusCounts[item.value] }}</span>
              </span>
            </template>
          </v-list-item>
        </template>
      </v-select>
      <v-select
        v-if="room"
        v-model="period"
        :items="periodOptions"
        density="compact"
        hide-details
        style="max-width: 180px"
        aria-label="Grading period"
      />
      <v-select
        v-if="room && isSHS"
        v-model="semester"
        :items="semesterOptions"
        density="compact"
        hide-details
        style="max-width: 180px"
        :aria-label="syType == 1 ? 'Grade level' : 'Semester'"
      />
    </template>
    <template #[`item.subjects`]="{ item }">
      <span v-if="isDropped(item) || !submissions?.subjects?.length" class="text-medium-emphasis">—</span>
      <v-menu v-else open-on-hover :open-delay="150" location="bottom start" offset="4">
        <template #activator="{ props: menuProps }">
          <button
            v-bind="menuProps"
            type="button"
            class="grades-progress"
            :aria-label="`${item.name}: ${gradesProgress(item).count} of ${gradesProgress(item).total} grades submitted`"
          >
            <UiBadge :variant="gradesProgress(item).variant">{{ gradesProgress(item).text }}</UiBadge>
          </button>
        </template>
        <v-card class="grades-list" elevation="3">
          <div class="grades-list__title">{{ periodLabel }} grades</div>
          <section v-for="group in gradesProgress(item).groups" :key="group.key" class="grades-list__group">
            <div class="grades-list__heading" :class="`grades-list__heading--${group.key}`">
              {{ group.title }} <span class="grades-list__count">{{ group.subjects.length }}</span>
            </div>
            <ul>
              <li v-for="subject in group.subjects" :key="subject.id">
                <v-icon
                  :icon="group.icon"
                  :color="group.key === 'submitted' ? 'success' : undefined"
                  size="16"
                  :class="{ 'text-medium-emphasis': group.key === 'missing' }"
                />
                <span class="grades-list__subject">
                  {{ subject.title }}
                  <span v-if="subject.teacher" class="grades-list__teacher">{{ subject.teacher }}</span>
                </span>
              </li>
            </ul>
          </section>
        </v-card>
      </v-menu>
    </template>
    <template #[`item.name`]="{ item }">
      <div class="d-flex align-center ga-2">
        <UiPerson :name="item.name" />
        <span v-if="isDropped(item)" :title="item.dropRemarks || ''">
          <UiBadge variant="amber">Dropped</UiBadge>
        </span>
      </div>
    </template>
    <template #[`item.valuesRated`]="{ item }">
      <span v-if="isDropped(item)" class="text-medium-emphasis">—</span>
      <span v-else :title="valuesProgress(item).detail">
        <UiBadge :variant="valuesProgress(item).variant">{{ valuesProgress(item).text }}</UiBadge>
      </span>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="dropOpen"
    title="Drop student"
    :description="dropping?.name"
    v-model:values="dropForm"
    :fields="dropFields"
    :saving="busy"
    submit-text="Drop student"
    @submit="drop"
  />

  <StudentGoodMoralDialog v-model="valuesOpen" :student="valuesFor" @saved="load" />
  <AddStudentList
    v-model="addOpen"
    :room-id="room?.id"
    :room-name="room?.name"
    :grade-level="room?.grade"
    :school-year-id="year"
    :existing-lrns="classLrns"
    @saved="load"
  />
  <ViewStudentFinalGradeDialog
    v-model="gradesOpen"
    :room="gradesRoom"
    :quarter="period"
    :semester="semester"
    :submissions="submissions"
    @saved="loadSubmissions"
  />
</template>

<style scoped>
/* The submissions badge opens the per-subject list */
.grades-progress {
  display: inline-flex;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}
.grades-list {
  min-width: 260px;
  max-width: 360px;
  padding: 10px 12px;
  font-size: 13px;
}
.grades-list__title {
  margin-bottom: 6px;
  font-weight: 600;
}
.grades-list ul {
  margin: 0;
  padding: 0;
  list-style: none;
}
.grades-list li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
}
.grades-list li .v-icon {
  margin-top: 2px;
}
.grades-list__subject {
  flex: 1;
  min-width: 0;
}
.grades-list__teacher {
  display: block;
  font-size: 12px;
  color: var(--muted-foreground);
}
.grades-list__group + .grades-list__group {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
}
.grades-list__heading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-foreground);
}
.grades-list__heading--missing {
  color: #b45309;
}
html.dark .grades-list__heading--missing {
  color: #fcd34d;
}
.grades-list__count {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--secondary-foreground);
  font-weight: 500;
  letter-spacing: 0;
  line-height: 16px;
  text-align: center;
}
/* Status filter option with its student count in a pill */
.status-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.status-option__count {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--secondary-foreground);
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-align: center;
}
</style>
